"use client";

import Link from "next/link";
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ClassIcon } from "@/components/icons/ClassIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { BattleNetIcon } from "@/components/icons/BattleNetIcon";
import { PageHeader } from "../_components/PageHeader";
import {
  useCharState,
  setContentFilter,
  resetCharData,
  setPlayedSpec,
} from "@/lib/state";
import { useAuth, logout, bnetLoginUrl } from "@/lib/auth";
import { CLASS_BY_ID, SPEC_BY_ID } from "@/lib/classes";
import { classColorVar, ROLE_LABEL } from "@/lib/wow";
import { cn } from "@/lib/utils";
import type { ContentFilter } from "@/lib/types";

export default function SettingsPage() {
  const state = useCharState();
  const auth = useAuth();
  const cls = CLASS_BY_ID[state.classID];
  const specEntry = SPEC_BY_ID[state.playedSpecID];

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader eyebrow="SETTINGS" title="Settings" />

      {/* Class & spec */}
      <Card>
        <CardHeader>
          <CardTitle>Class & spec</CardTitle>
        </CardHeader>
        <CardBody>
          {cls && specEntry ? (
            <div className="flex items-center gap-4">
              <ClassIcon classToken={cls.token} size={48} />
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg" style={{ color: classColorVar(cls.token) }}>
                  {cls.name}
                </p>
                <div className="mt-0.5 flex items-center gap-2">
                  <SpecIcon specID={specEntry.spec.id} size={18} />
                  <span className="text-sm text-fg-muted">
                    {specEntry.spec.name} ({ROLE_LABEL[specEntry.spec.role]})
                  </span>
                </div>
              </div>
              <Link
                href="/"
                className="inline-flex items-center justify-center px-4 h-9 rounded-sm border border-border-strong text-sm text-fg hover:bg-surface-2 transition-colors"
              >
                Change class
              </Link>
            </div>
          ) : null}

          <hr className="hairline my-5" />

          <div>
            <p className="eyebrow mb-2">Other specs of {cls?.name ?? "this class"}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {cls?.specs.map((s) => {
                const active = s.id === state.playedSpecID;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setPlayedSpec(s.id)}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-sm border text-left transition-colors",
                      active
                        ? "border-accent bg-surface-2"
                        : "border-border bg-surface hover:border-border-strong",
                    )}
                  >
                    <SpecIcon specID={s.id} size={28} />
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: classColorVar(cls.token) }}>
                        {s.name}
                      </p>
                      <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                        {ROLE_LABEL[s.role]}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Content filter */}
      <Card>
        <CardHeader>
          <CardTitle>Content filter</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-xs text-fg-muted mb-3">
            Limit recommendations to a specific content type.
          </p>
          <div className="inline-flex items-center gap-1 rounded-sm border border-border bg-surface p-0.5">
            {([
              ["both", "All content"],
              ["mplus", "Mythic+ only"],
              ["raid", "Raid only"],
            ] as [ContentFilter, string][]).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setContentFilter(v)}
                className={cn(
                  "h-9 px-4 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
                  state.contentFilter === v
                    ? "bg-surface-3 text-fg"
                    : "text-fg-subtle hover:text-fg hover:bg-surface-2",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Battle.net */}
      <Card>
        <CardHeader>
          <CardTitle>Battle.net</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex items-center justify-between gap-4 rounded-sm border border-border bg-surface-2/40 p-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-[#1f9bff]/40 bg-[#148eff]/15 text-[#148eff]">
                <BattleNetIcon size={18} />
              </span>
              <div className="min-w-0">
                {auth.state === "signed-in" ? (
                  <>
                    <p className="text-sm text-fg break-all">{auth.user.battletag}</p>
                    <p className="text-xs text-fg-subtle font-mono uppercase tracking-wider">
                      Connected to {auth.user.region.toUpperCase()} region
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-fg">
                      {auth.state === "loading" ? "Checking..." : "Not connected"}
                    </p>
                    <p className="text-xs text-fg-subtle font-mono">
                      Sign in to import character gear and persist data across devices.
                    </p>
                  </>
                )}
              </div>
            </div>

            {auth.state === "signed-in" ? (
              <Button variant="outline" size="md" onClick={() => void logout()}>
                Disconnect
              </Button>
            ) : (
              <a
                href={bnetLoginUrl(state.equippedMyth ? "eu" : "eu")}
                className="inline-flex items-center justify-center px-4 h-9 rounded-sm bg-[#148eff] text-white text-sm hover:bg-[#1f9bff] transition-colors"
              >
                Connect
              </a>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Reset */}
      <Card>
        <CardHeader>
          <CardTitle>Local data</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-fg-muted">
            Your BiS targets and exclusions are stored in this browser. Clear them to start over.
          </p>
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button
            variant="outline"
            size="md"
            onClick={() => {
              if (confirm("Reset all BiS targets and exclusions? This can't be undone.")) {
                resetCharData();
              }
            }}
          >
            Reset BiS + exclusions
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
